import * as m from '@staiter/i18n';
import type { Payment } from '@staiter/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeftRight, CheckCircle2, ExternalLink, Plus, Wallet } from 'lucide-react';
import { useMemo, useState } from 'react';
import { EmptyState, PageHeader, Pagination, Table, type TableColumn } from '~/components/data';
import { PeriodCloseBanner } from '~/components/domain';
import { useToast } from '~/components/feedback';
import { Button, Select } from '~/components/primitives';
import { StatusBadge, type StatusBadgeStatus } from '~/components/status';
import { ProvenanceChip } from '~/components/status/ProvenanceChip';
import { type PaymentListFilter, bulkPaymentAction, fetchPayments } from '~/lib/api/payments';
import { canDo, useUserScope } from '~/lib/rbac/use-user-scope';

// FE-P2-2 (QA 2026-06-04): payment state → StatusBadge variant.
const PAYMENT_STATE_BADGE: Record<string, StatusBadgeStatus> = {
  draft: 'needs_review',
  posted: 'completed_draft',
  reconciled: 'completed',
  cancelled: 'failed',
  void: 'failed',
};
import { renderApiErrorMessage } from '~/lib/api/error-renderer';
import { formatDate, formatMoney } from '~/lib/format';
import shared from './_auth._shared.module.css';

/**
 * S14-FE.7 — Payments list page.
 */

export const Route = createFileRoute('/_auth/payments/')({
  staticData: { minMainWidth: 960 },
  component: PaymentsPage,
});

const DIRECTION_LABELS = {
  inflow: m.payment_direction_inflow,
  outflow: m.payment_direction_outflow,
} as const;
const METHOD_LABELS: Record<string, () => string> = {
  bank_transfer: m.payment_method_bank_transfer,
  cash: m.payment_method_cash,
  card: m.payment_method_card,
  offset: m.payment_method_offset,
  other: m.payment_method_other,
};

function PaymentsPage(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  // S-FIX-5-FE.13.1: pagination state.
  const [pagination, setPagination] = useState<{ limit: number; offset: number }>({
    limit: 50,
    offset: 0,
  });
  // FE-P2-1 (pass3 closure): direction + state filters.
  const [filters, setFilters] = useState<
    Pick<PaymentListFilter, 'direction' | 'state' | 'source_system' | 'reconcile_status'>
  >({});
  // SET-AUDIT-7: selection state for bulk actions.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const query = useQuery({
    queryKey: [
      'payments',
      pagination.limit,
      pagination.offset,
      filters.direction,
      filters.state,
      filters.source_system,
      filters.reconcile_status,
    ],
    queryFn: () => fetchPayments({ ...pagination, ...filters }),
  });
  const rows = query.data?.items ?? [];
  const totalCount =
    (query.data as { total_count?: number } | undefined)?.total_count ?? rows.length;

  // SET-AUDIT-7: bulk post payments.
  const bulkMut = useMutation({
    mutationFn: bulkPaymentAction,
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['payments'] });
      setSelected(new Set());
      const skippedCount = res.skipped.length;
      const skippedHint =
        skippedCount > 0 ? ` Пропущено: ${skippedCount} (не draft или отсутствует)` : '';
      toast.push({
        variant: skippedCount === res.total ? 'warning' : 'success',
        title: `Проведено: ${res.succeeded.length}/${res.total}`,
        description: skippedHint || undefined,
      });
    },
    onError: (err) => {
      toast.push({
        variant: 'error',
        title: 'Массовая операция не выполнена',
        description: renderApiErrorMessage(err),
      });
    },
  });

  // access-control (F9) — колонка компании только в holding-агрегате.
  const entityNames =
    (query.data as { entities?: Record<string, { name: string }> } | undefined)?.entities ?? {};
  const showCompanyCol = new Set(rows.map((r) => r.entity_id)).size > 1;

  const columns: TableColumn<Payment>[] = useMemo(
    () => [
      ...(showCompanyCol
        ? [
            {
              id: 'company',
              header: m.cp_field_company(),
              accessor: (r: Payment) => entityNames[r.entity_id]?.name ?? '—',
              width: '150px',
            } satisfies TableColumn<Payment>,
          ]
        : []),
      {
        id: 'date',
        header: m.payment_column_date(),
        accessor: (r) => (
          <Link to="/payments/$id" params={{ id: r.id }} className={shared.accentLink}>
            {formatDate(r.payment_date)}
          </Link>
        ),
        width: '120px',
        sticky: true,
      },
      {
        id: 'direction',
        header: m.payment_column_direction(),
        // integrations F8 — internal_transfer как явный бейдж (исключён из ДДС-итогов).
        accessor: (r) =>
          (r as { internal_transfer?: boolean }).internal_transfer ? (
            <span className={shared.transferBadge} title={m.payment_badge_transfer()}>
              <ArrowLeftRight size={11} aria-hidden="true" />
              {m.payment_badge_transfer()}
            </span>
          ) : (
            DIRECTION_LABELS[r.direction]()
          ),
        width: '130px',
      },
      {
        id: 'cp',
        header: m.payment_column_counterparty(),
        // S-FIX-5-FE.4.1: counterparty_name_ru/en denormalized BE.1.2.
        // F101 (С-I, гейт круга 4, UI MAJOR): имя контрагента переносилось в три строки — строка
        // реестра 90 px вместо 40. Одной строкой с многоточием и полным именем в подсказке; ширина
        // явная, как у соседей (`minWidth` в автоматической раскладке таблицы не действует).
        accessor: (r) => {
          const name =
            (r as { counterparty_name_ru?: string | null }).counterparty_name_ru ??
            (r as { counterparty_name_en?: string | null }).counterparty_name_en ??
            r.counterparty_id.slice(0, 8);
          return (
            <span className={shared.cellEllipsis} title={name}>
              {name}
            </span>
          );
        },
        sortValue: (r) =>
          (r as { counterparty_name_ru?: string | null }).counterparty_name_ru ??
          (r as { counterparty_name_en?: string | null }).counterparty_name_en ??
          r.counterparty_id,
        // Запас поля автоматическая раскладка делит пропорционально объявленным ширинам: опознающей
        // колонке — самая большая доля (замер на 1280 — тело кадра гейта F101).
        width: '280px',
      },
      {
        // integrations F8 — статья/группа ДДС + проект из Adesk-операций.
        id: 'cf',
        header: m.payment_column_cf(),
        accessor: (r) => {
          const cf = r as {
            cf_article?: string | null;
            cf_group?: string | null;
            project_tag?: string | null;
          };
          if (!cf.cf_article && !cf.cf_group && !cf.project_tag) return '—';
          const text = `${cf.cf_article ?? cf.cf_group ?? '—'}${cf.project_tag ? ` · ${cf.project_tag}` : ''}`;
          // F101 (гейт круга 5, UI): статья из Adesk одной строкой с многоточием — иначе перенос
          // снова растил строку реестра.
          return (
            <span
              className={`${shared.fs11Subtle} ${shared.cellEllipsis} ${shared.cellEllipsisNarrow}`}
              title={text}
            >
              {text}
            </span>
          );
        },
        width: '120px',
      },
      {
        id: 'amount',
        header: m.payment_column_amount(),
        // FE-297 Pass 65: nowrap (money wraps на 3 строки в narrow column).
        accessor: (r) => <span style={{ whiteSpace: 'nowrap' }}>{formatMoney(r.amount)}</span>,
        align: 'right',
        mono: true,
        width: '140px',
      },
      {
        id: 'method',
        header: m.payment_column_method(),
        accessor: (r) => (METHOD_LABELS[r.method] ?? (() => r.method))(),
        width: '130px',
        // F101 (гейт круга 4): «Банк. перевод» переносилось в две строки — строка реестра 66 px.
        nowrap: true,
      },
      {
        id: 'state',
        header: m.payment_column_state(),
        accessor: (r) => (
          <StatusBadge status={PAYMENT_STATE_BADGE[r.state] ?? 'needs_review'} size="sm" />
        ),
        // F101 (гейт круга 5, UI): бейдж «Требует проверки» — 137 px; объявленные 110 px раскладка
        // всё равно расширяла, а лишнее распределяла мимо «Контрагента».
        width: '165px',
      },
      {
        id: 'source',
        header: m.payment_column_source(),
        // integrations F8 — ProvenanceChip источника (adesk/adesk_seed/bitrix/1c/staiter).
        accessor: (r) => {
          const rs = (r as { reconcile_status?: string }).reconcile_status;
          return (
            <span className={shared.sourceCell}>
              <ProvenanceChip source={r.source_system} />
              {rs && rs !== 'live' ? (
                <span className={shared.reconcileBadge} title={m.payment_reconcile_match_hint()}>
                  {m.payment_reconcile_label({ status: rs })}
                </span>
              ) : null}
            </span>
          );
        },
        // F101 (гейт круга 5, UI MAJOR): объявленные 200 px при содержимом ≈ 90 забирали в
        // автоматической раскладке запас, а имя контрагента рядом обрезалось многоточием.
        width: '120px',
      },
    ],
    [showCompanyCol, entityNames],
  );

  const scope = useUserScope();
  const canCreate = canDo(scope, 'payment', 'create');
  return (
    <div>
      <PageHeader
        title={m.phase2_payments_title()}
        actions={
          // F38 (WHS-F38): создание — только роли с правом `payment:create`.
          canCreate ? (
            <Button
              variant="primary"
              leadingIcon={<Plus size={16} />}
              onClick={() => void navigate({ to: '/payments/new' })}
            >
              {m.payment_button_new()}
            </Button>
          ) : undefined
        }
      />
      <PeriodCloseBanner scope="payment" />
      {/* integrations F9/B17 — sanity-баннер с числом исключённых (evidence-first). */}
      {(filters.reconcile_status === 'superseded' || filters.reconcile_status === 'seed_only') && (
        <div className={shared.reconcileBanner}>
          {m.payment_reconcile_banner({ count: query.data?.total_count ?? rows.length })}
        </div>
      )}
      {/* FE-P2-1 (pass3 closure): filter row для list view. */}
      <div className={shared.filters}>
        <Select
          label={m.payment_column_direction()}
          hideLabel
          size="sm"
          value={filters.direction ?? ''}
          placeholder={m.payment_column_direction()}
          options={[
            { value: '', label: m.common_filter_all_directions() },
            { value: 'inflow', label: m.payment_direction_inflow() },
            { value: 'outflow', label: m.payment_direction_outflow() },
          ]}
          onChange={(e) => {
            const v = e.target.value;
            setFilters((f) => ({
              ...f,
              direction: v === '' ? undefined : (v as 'inflow' | 'outflow'),
            }));
            setPagination((p) => ({ ...p, offset: 0 }));
          }}
        />
        <Select
          label={m.payment_column_state()}
          hideLabel
          size="sm"
          value={filters.state ?? ''}
          placeholder={m.payment_column_state()}
          options={[
            { value: '', label: m.so_filter_all_statuses() },
            { value: 'draft', label: 'Черновик' },
            { value: 'posted', label: 'Проведено' },
            { value: 'reconciled', label: 'Сверено' },
            { value: 'cancelled', label: 'Отменено' },
          ]}
          onChange={(e) => {
            const v = e.target.value;
            setFilters((f) => ({
              ...f,
              state: v === '' ? undefined : (v as PaymentListFilter['state']),
            }));
            setPagination((p) => ({ ...p, offset: 0 }));
          }}
        />
        {/* integrations F8 — provenance-фильтр. */}
        <Select
          label={m.payment_filter_source()}
          hideLabel
          size="sm"
          value={filters.source_system ?? ''}
          placeholder={m.payment_filter_source()}
          options={[
            { value: '', label: m.payment_filter_all_sources() },
            { value: 'staiter', label: 'STAITER' },
            { value: 'adesk', label: 'Adesk' },
            { value: 'adesk_seed', label: 'Adesk (seed)' },
            { value: 'bitrix', label: 'Bitrix' },
            { value: 'bank_import', label: m.payment_source_bank_import() },
          ]}
          onChange={(e) => {
            const v = e.target.value;
            setFilters((f) => ({ ...f, source_system: v === '' ? undefined : v }));
            setPagination((p) => ({ ...p, offset: 0 }));
          }}
        />
        {/* integrations F9/B17 — reconcile-фильтр (test-only safety-net). */}
        <Select
          label={m.payment_reconcile_filter()}
          hideLabel
          size="sm"
          value={filters.reconcile_status ?? ''}
          placeholder={m.payment_reconcile_filter()}
          options={[
            { value: '', label: m.payment_reconcile_all() },
            { value: 'live', label: m.payment_reconcile_label({ status: 'live' }) },
            { value: 'superseded', label: m.payment_reconcile_label({ status: 'superseded' }) },
            { value: 'seed_only', label: m.payment_reconcile_label({ status: 'seed_only' }) },
          ]}
          onChange={(e) => {
            const v = e.target.value;
            setFilters((f) => ({
              ...f,
              reconcile_status: v === '' ? undefined : (v as 'live' | 'superseded' | 'seed_only'),
            }));
            setPagination((p) => ({ ...p, offset: 0 }));
          }}
        />
      </div>
      {rows.length === 0 && !query.isLoading ? (
        <EmptyState title={m.payment_empty_title()} icon={<Wallet size={32} />} />
      ) : (
        <>
          <Table<Payment>
            data={rows}
            columns={columns}
            rowKey={(r) => r.id}
            ariaLabel={m.phase2_payments_title()}
            loading={query.isLoading}
            emptyTitle={m.payment_empty_title()}
            stickyFirstColumn
            selectable
            selectedIds={selected}
            onSelectionChange={setSelected}
            rowActions={(r) => (
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(`/payments/${r.id}`, '_blank', 'noopener');
                }}
                title={m.ui_otkryt_v_novoy_vkladke()}
                aria-label={m.ui_otkryt_v_novoy_vkladke()}
              >
                {/* F101 (гейт круга 6, UI+UX MAJOR): только значок — кнопка с подписью держала
                    ≈ 129 px колонки, видимой лишь при наведении, и на 1280 обрезала имя
                    контрагента; в карточку ведёт ссылка в «Дате», имя и подсказка — в
                    `aria-label`/`title`. */}
                <ExternalLink size={14} aria-hidden="true" />
              </Button>
            )}
          />
          <Pagination
            totalCount={totalCount}
            limit={pagination.limit}
            offset={pagination.offset}
            onChange={setPagination}
          />
        </>
      )}
      {/* SET-AUDIT-7: sticky bulk action bar при ≥1 selected. */}
      {selected.size > 0 && (
        <div
          role="toolbar"
          aria-label="Массовые действия"
          style={{
            position: 'fixed',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--bg-elevated)',
            border: '1px solid var(--color-border)',
            borderRadius: 8,
            padding: '10px 16px',
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
            zIndex: 100,
          }}
        >
          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            {m.ui_vybrano()} <strong>{selected.size}</strong>
          </span>
          <Button
            variant="ghost"
            size="sm"
            leadingIcon={<CheckCircle2 size={14} aria-hidden="true" />}
            onClick={() => bulkMut.mutate({ ids: Array.from(selected), action: 'post' })}
            disabled={bulkMut.isPending}
          >
            {m.ui_provesti()}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
            {m.ui_snyat_vydelenie()}
          </Button>
        </div>
      )}
    </div>
  );
}
